import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/Button';
import { EditOnly } from '@/components/EditOnly';
import { Modal } from '@/components/Modal';
import { Select } from '@/components/Select';
import {
  listSchedules,
  createSchedule,
  updateSchedule,
  deleteSchedule,
  previewAudience,
  sendNow,
  testRun,
  listPushApps,
  listTemplates,
  broadcastAudience,
  sendBroadcast,
  type PushSchedule,
  type BroadcastResult,
  type BroadcastTarget,
} from '@/api/push';
import { getPwaApps } from '@/api/pwa';
import { getTrackerPwas } from '@/api/tracker-pwas';
import type { PwaApp } from '@/api/types';
import type { TrackerPwa } from '@/api/tracker-types';

// ─── Helpers ──────────────────────────────────────────────────────────────────

// A schedule (or broadcast) targets exactly one of three things. The two PWA
// kinds are separate entities: storefronts under /pwa/:id and the campaign
// landings served on tracker domains.
type TargetType = 'app' | 'pwa' | 'tracker_pwa';

const TARGET_TYPE_OPTIONS: { value: TargetType; label: string }[] = [
  { value: 'app', label: 'App' },
  { value: 'pwa', label: 'PWA' },
  { value: 'tracker_pwa', label: 'Tracker PWA' },
];

const TARGET_TYPE_LABEL: Record<TargetType, string> = {
  app: 'App',
  pwa: 'PWA',
  tracker_pwa: 'Tracker PWA',
};

function minutesToTime(mins: number): string {
  const h = Math.floor(mins / 60).toString().padStart(2, '0');
  const m = (mins % 60).toString().padStart(2, '0');
  return `${h}:${m}`;
}

function timeToMinutes(time: string): number {
  const [h, m] = time.split(':').map(Number);
  return (h ?? 0) * 60 + (m ?? 0);
}

const STATUS_LABELS: Record<string, string> = {
  draft: 'Draft',
  active: 'Active',
  paused: 'Paused',
  completed: 'Completed',
  deleted: 'Deleted',
};

const STATUS_COLORS: Record<string, string> = {
  draft: 'text-content-subtle',
  active: 'text-success',
  paused: 'text-warning',
  completed: 'text-info',
  deleted: 'text-danger',
};

// ─── Schedule Editor Form ─────────────────────────────────────────────────────

interface FormState {
  name: string;
  app_id: string;
  template_id: string;
  kind: 'once' | 'daily' | 'weekly';
  send_at_time: string; // HH:MM for <input type="time">
  once_date: string;
  weekly_days: string;
  starts_on: string;
  ends_on: string;
  target_countries: string;
  target_locales: string;
  target_min_app_version: string;
  target_max_app_version: string;
  exclude_blocked: boolean;
  status: 'draft' | 'active' | 'paused' | 'completed';
}

function blankForm(): FormState {
  return {
    name: '',
    app_id: '',
    template_id: '',
    kind: 'daily',
    send_at_time: '09:00',
    once_date: '',
    weekly_days: '',
    starts_on: '',
    ends_on: '',
    target_countries: '',
    target_locales: '',
    target_min_app_version: '',
    target_max_app_version: '',
    exclude_blocked: true,
    status: 'draft',
  };
}

function formFromSchedule(s: PushSchedule): FormState {
  return {
    name: s.name,
    app_id: s.app_id ?? '',
    template_id: s.template_id,
    kind: s.kind,
    send_at_time: minutesToTime(s.send_at_local_minutes),
    once_date: s.once_date ?? '',
    weekly_days: s.weekly_days ?? '',
    starts_on: s.starts_on ?? '',
    ends_on: s.ends_on ?? '',
    target_countries: s.target_countries ?? '',
    target_locales: s.target_locales ?? '',
    target_min_app_version: s.target_min_app_version ?? '',
    target_max_app_version: s.target_max_app_version ?? '',
    exclude_blocked: Boolean(s.exclude_blocked),
    status: s.status as FormState['status'],
  };
}

function formToBody(f: FormState): Partial<PushSchedule> {
  const mins = timeToMinutes(f.send_at_time);
  return {
    name: f.name.trim(),
    app_id: f.app_id,
    template_id: f.template_id,
    kind: f.kind,
    send_at_local_minutes: mins,
    once_date: f.kind === 'once' ? f.once_date || null : null,
    weekly_days: f.kind === 'weekly' ? f.weekly_days.trim() || null : null,
    starts_on: f.starts_on || null,
    ends_on: f.ends_on || null,
    target_countries: f.target_countries.trim() || null,
    target_locales: f.target_locales.trim() || null,
    target_min_app_version: f.target_min_app_version.trim() || null,
    target_max_app_version: f.target_max_app_version.trim() || null,
    exclude_blocked: f.exclude_blocked ? 1 : 0,
    status: f.status,
  };
}

const inputCls =
  'block w-full px-3 py-2 border border-border-strong rounded-md shadow-sm text-sm focus:outline-none focus:ring-primary focus:border-primary';

function ScheduleEditorModal({
  schedule,
  onClose,
}: {
  schedule: PushSchedule | null; // null = create
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const isCreate = schedule === null;
  const [form, setForm] = useState<FormState>(() =>
    schedule ? formFromSchedule(schedule) : blankForm()
  );
  const [error, setError] = useState('');
  const [targetType, setTargetType] = useState<TargetType>('app');
  const [pwaApps, setPwaApps] = useState<PwaApp[]>([]);
  const [trackerPwas, setTrackerPwas] = useState<TrackerPwa[]>([]);
  // One id box for both PWA kinds — targetType says which list it came from.
  const [pwaId, setPwaId] = useState<string>('');

  useEffect(() => {
    getPwaApps().then(setPwaApps).catch(() => setPwaApps([]));
    getTrackerPwas().then(setTrackerPwas).catch(() => setTrackerPwas([]));
  }, []);

  const pwaOptions = targetType === 'tracker_pwa'
    ? trackerPwas.filter((p) => p.push_enabled).map((p) => ({ value: p.id, label: p.name }))
    : pwaApps.filter((p) => p.push_enabled).map((p) => ({ value: p.id, label: p.app_name }));

  const { data: appsData } = useQuery({ queryKey: ['push-apps'], queryFn: listPushApps });
  const apps = appsData?.apps ?? [];

  const { data: tmplData } = useQuery({ queryKey: ['push-templates'], queryFn: listTemplates });
  const templates = tmplData?.templates ?? [];

  const inval = () => queryClient.invalidateQueries({ queryKey: ['push-schedules'] });

  const createMut = useMutation({
    mutationFn: (body: Partial<PushSchedule>) => createSchedule(body),
    onSuccess: () => { inval(); onClose(); },
    onError: (err: Error) => setError(err.message),
  });

  const updateMut = useMutation({
    mutationFn: ({ id, body }: { id: string; body: Partial<PushSchedule> }) =>
      updateSchedule(id, body),
    onSuccess: () => { inval(); onClose(); },
    onError: (err: Error) => setError(err.message),
  });

  const isPending = createMut.isPending || updateMut.isPending;

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    const mins = timeToMinutes(form.send_at_time);
    if (mins % 30 !== 0) {
      setError('Send time must be a multiple of 30 minutes (e.g. 09:00, 09:30).');
      return;
    }
    if (!form.name.trim()) { setError('Name is required.'); return; }
    if (isCreate) {
      if (targetType !== 'app' && !pwaId) { setError('PWA is required.'); return; }
      if (targetType === 'app' && !form.app_id) { setError('App is required.'); return; }
    } else {
      if (!form.app_id && !schedule?.pwa_id && !schedule?.tracker_pwa_id) { setError('Target is required.'); return; }
    }
    if (!form.template_id) { setError('Template is required.'); return; }

    const baseBody = formToBody(form);
    let body: Partial<PushSchedule>;
    if (isCreate) {
      // Strip app_id from base; set exactly one of the three target ids.
      const { app_id: _dropped, ...rest } = baseBody;
      void _dropped;
      const target =
        targetType === 'pwa' ? { pwa_id: pwaId }
        : targetType === 'tracker_pwa' ? { tracker_pwa_id: pwaId }
        : { app_id: form.app_id };
      body = { ...rest, ...target };
    } else {
      body = baseBody;
    }

    if (schedule) {
      updateMut.mutate({ id: schedule.id, body });
    } else {
      createMut.mutate(body);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {error && (
        <div className="p-3 bg-danger-soft border border-danger/30 rounded-md text-sm text-danger">{error}</div>
      )}

      {/* Name */}
      <div>
        <label className="block text-sm font-medium text-content-muted mb-1">Name</label>
        <input
          autoFocus
          type="text"
          value={form.name}
          onChange={(e) => set('name', e.target.value)}
          placeholder="e.g. daily-promo"
          className={inputCls}
        />
      </div>

      {/* Target type toggle (create only) */}
      {isCreate && (
        <div>
          <label className="block text-sm font-medium text-content-muted mb-1">Target type</label>
          <Select
            value={targetType}
            onChange={(v) => { setTargetType(v as TargetType); setPwaId(''); }}
            options={TARGET_TYPE_OPTIONS}
          />
        </div>
      )}

      {/* App + Template */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          {isCreate && targetType !== 'app' ? (
            <>
              <label className="block text-sm font-medium text-content-muted mb-1">
                {targetType === 'tracker_pwa' ? 'Tracker PWA' : 'PWA'}
              </label>
              <Select
                value={pwaId}
                onChange={(v) => setPwaId(v)}
                placeholder="— select PWA —"
                options={[{ value: '', label: '— select PWA —' }, ...pwaOptions]}
                searchable
              />
            </>
          ) : (
            <>
              <label className="block text-sm font-medium text-content-muted mb-1">App</label>
              <Select
                value={form.app_id}
                onChange={(v) => set('app_id', v)}
                placeholder="— select app —"
                options={[
                  { value: '', label: '— select app —' },
                  ...apps.map((a) => ({ value: a.id, label: a.name })),
                ]}
                searchable
              />
            </>
          )}
        </div>
        <div>
          <label className="block text-sm font-medium text-content-muted mb-1">Template</label>
          <Select
            value={form.template_id}
            onChange={(v) => set('template_id', v)}
            placeholder="— select template —"
            options={[
              { value: '', label: '— select template —' },
              ...templates.map((t) => ({ value: t.id, label: t.name })),
            ]}
            searchable
          />
        </div>
      </div>

      {/* Kind + Time */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="block text-sm font-medium text-content-muted mb-1">Kind</label>
          <Select
            value={form.kind}
            onChange={(v) => set('kind', v as FormState['kind'])}
            options={[
              { value: 'once', label: 'Once' },
              { value: 'daily', label: 'Daily' },
              { value: 'weekly', label: 'Weekly' },
            ]}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-content-muted mb-1">Local send time</label>
          <input
            type="time"
            step={1800}
            value={form.send_at_time}
            onChange={(e) => set('send_at_time', e.target.value)}
            className={inputCls}
          />
          <p className="mt-1 text-xs text-content-subtle">Must be a 30-minute interval (e.g. 09:00 or 09:30).</p>
        </div>
      </div>

      {/* Kind-specific fields */}
      {form.kind === 'once' && (
        <div>
          <label className="block text-sm font-medium text-content-muted mb-1">Send date</label>
          <input
            type="date"
            value={form.once_date}
            onChange={(e) => set('once_date', e.target.value)}
            className={inputCls}
          />
        </div>
      )}
      {form.kind === 'weekly' && (
        <div>
          <label className="block text-sm font-medium text-content-muted mb-1">Weekly days (CSV)</label>
          <input
            type="text"
            value={form.weekly_days}
            onChange={(e) => set('weekly_days', e.target.value)}
            placeholder="mon,wed,fri"
            className={inputCls}
          />
          <p className="mt-1 text-xs text-content-subtle">Comma-separated: mon, tue, wed, thu, fri, sat, sun</p>
        </div>
      )}

      {/* Date range */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="block text-sm font-medium text-content-muted mb-1">Starts on (optional)</label>
          <input
            type="date"
            value={form.starts_on}
            onChange={(e) => set('starts_on', e.target.value)}
            className={inputCls}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-content-muted mb-1">Ends on (optional)</label>
          <input
            type="date"
            value={form.ends_on}
            onChange={(e) => set('ends_on', e.target.value)}
            className={inputCls}
          />
        </div>
      </div>

      {/* Targeting */}
      <div className="pt-1">
        <p className="text-xs font-semibold text-content-subtle uppercase tracking-wide mb-2">Targeting</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-medium text-content-muted mb-1">Countries (CSV, blank=all)</label>
            <input
              type="text"
              value={form.target_countries}
              onChange={(e) => set('target_countries', e.target.value)}
              placeholder="US,GB,DE"
              className={inputCls}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-content-muted mb-1">Locales (CSV, blank=all)</label>
            <input
              type="text"
              value={form.target_locales}
              onChange={(e) => set('target_locales', e.target.value)}
              placeholder="en,de,fr"
              className={inputCls}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-content-muted mb-1">Min app version</label>
            <input
              type="text"
              value={form.target_min_app_version}
              onChange={(e) => set('target_min_app_version', e.target.value)}
              placeholder="1.0.0"
              className={inputCls}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-content-muted mb-1">Max app version</label>
            <input
              type="text"
              value={form.target_max_app_version}
              onChange={(e) => set('target_max_app_version', e.target.value)}
              placeholder="2.0.0"
              className={inputCls}
            />
          </div>
        </div>
        <div className="mt-3 flex items-center gap-2">
          <input
            id="exclude_blocked"
            type="checkbox"
            checked={form.exclude_blocked}
            onChange={(e) => set('exclude_blocked', e.target.checked)}
            className="h-4 w-4 text-primary border-border-strong rounded focus:ring-primary"
          />
          <label htmlFor="exclude_blocked" className="text-sm text-content-muted">
            Exclude blocked devices
          </label>
        </div>
      </div>

      {/* Status */}
      <div>
        <label className="block text-sm font-medium text-content-muted mb-1">Status</label>
        <Select
          value={form.status}
          onChange={(v) => set('status', v as FormState['status'])}
          options={[
            { value: 'draft', label: 'Draft' },
            { value: 'active', label: 'Active' },
            { value: 'paused', label: 'Paused' },
            { value: 'completed', label: 'Completed' },
          ]}
        />
      </div>

      <div className="flex justify-end gap-3 pt-2">
        <Button type="button" variant="secondary" onClick={onClose}>Cancel</Button>
        <Button type="submit" loading={isPending}>
          {schedule ? 'Save' : 'Create'}
        </Button>
      </div>
    </form>
  );
}

// ─── Broadcast Modal ──────────────────────────────────────────────────────────
// A one-off "send this text to everyone, now". Deliberately has no template,
// schedule or targeting: anything more than that is a schedule, and schedules
// already exist. Since a broadcast cannot be recalled, Send is a two-step
// action showing the exact recipient count before it fires.

function BroadcastModal({ onClose }: { onClose: () => void }) {
  const [targetType, setTargetType] = useState<TargetType>('app');
  const [appId, setAppId] = useState('');
  const [pwaId, setPwaId] = useState('');
  const [title, setTitle] = useState('');
  const [text, setText] = useState('');
  const [includeTest, setIncludeTest] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState<BroadcastResult | null>(null);

  const { data: appsData } = useQuery({ queryKey: ['push-apps'], queryFn: listPushApps });
  const apps = (appsData?.apps ?? []).filter((a) => a.push_firebase_project_id);

  const [pwaApps, setPwaApps] = useState<PwaApp[]>([]);
  const [trackerPwas, setTrackerPwas] = useState<TrackerPwa[]>([]);
  useEffect(() => {
    getPwaApps().then(setPwaApps).catch(() => setPwaApps([]));
    getTrackerPwas().then(setTrackerPwas).catch(() => setTrackerPwas([]));
  }, []);
  const pushPwas = targetType === 'tracker_pwa'
    ? trackerPwas.filter((p) => p.push_enabled).map((p) => ({ id: p.id, label: p.name }))
    : pwaApps.filter((p) => p.push_enabled).map((p) => ({ id: p.id, label: p.app_name }));

  const targetId = targetType === 'app' ? appId : pwaId;
  const target: BroadcastTarget =
    targetType === 'pwa' ? { pwa_id: pwaId }
    : targetType === 'tracker_pwa' ? { tracker_pwa_id: pwaId }
    : { app_id: appId };

  const { data: audienceData, isFetching: countingAudience } = useQuery({
    queryKey: ['push-broadcast-audience', targetType, targetId, includeTest],
    queryFn: () => broadcastAudience(target, includeTest),
    enabled: !!targetId,
  });
  const audience = audienceData?.audience ?? 0;

  const sendMut = useMutation({
    mutationFn: () => sendBroadcast({ ...target, title: title.trim(), body: text.trim(), include_test: includeTest }),
    onSuccess: setResult,
    onError: (err: Error) => { setError(err.message); setConfirming(false); },
  });

  // Any edit invalidates the confirmation the admin just gave.
  const edit = <T,>(setter: (v: T) => void) => (v: T) => { setter(v); setConfirming(false); setError(''); };

  const ready = !!targetId && !!title.trim() && !!text.trim() && audience > 0;

  if (result) {
    return (
      <div className="space-y-4">
        <div className="text-sm text-content">
          Broadcast sent to {result.audience.toLocaleString()} device(s).
        </div>
        <dl className="grid grid-cols-2 gap-2 text-sm">
          <dt className="text-content-muted">Sent</dt><dd className="text-success">{result.sent}</dd>
          <dt className="text-content-muted">Failed</dt><dd className="text-danger">{result.failed}</dd>
          <dt className="text-content-muted">Token invalid</dt><dd className="text-warning">{result.token_invalid}</dd>
          <dt className="text-content-muted">Skipped</dt><dd className="text-content-subtle">{result.skipped}</dd>
        </dl>
        <div className="flex justify-end">
          <Button onClick={onClose}>Close</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {error && <div className="text-sm text-danger">{error}</div>}

      <div>
        <label className="block text-sm font-medium text-content-muted mb-1">Target type</label>
        <Select
          value={targetType}
          onChange={(v) => { edit(setTargetType)(v as TargetType); setAppId(''); setPwaId(''); }}
          options={TARGET_TYPE_OPTIONS}
        />
      </div>

      {targetType !== 'app' ? (
        <div>
          <label className="block text-sm font-medium text-content-muted mb-1">
            {TARGET_TYPE_LABEL[targetType]}
          </label>
          <Select
            value={pwaId}
            onChange={edit(setPwaId)}
            searchable
            options={[
              { value: '', label: 'Select a PWA…' },
              ...pushPwas.map((p) => ({ value: p.id, label: p.label })),
            ]}
          />
        </div>
      ) : (
        <div>
          <label className="block text-sm font-medium text-content-muted mb-1">App</label>
          <Select
            value={appId}
            onChange={edit(setAppId)}
            options={[
              { value: '', label: 'Select an app…' },
              ...apps.map((a) => ({ value: a.id, label: a.name })),
            ]}
          />
        </div>
      )}

      <div>
        <label className="block text-sm font-medium text-content-muted mb-1">Title</label>
        <input
          type="text"
          value={title}
          onChange={(e) => edit(setTitle)(e.target.value)}
          className={inputCls}
          maxLength={120}
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-content-muted mb-1">Text</label>
        <textarea
          value={text}
          onChange={(e) => edit(setText)(e.target.value)}
          rows={3}
          className={inputCls}
          maxLength={400}
        />
      </div>

      <label className="flex items-center gap-2 text-sm text-content-muted">
        <input
          type="checkbox"
          checked={includeTest}
          onChange={(e) => edit(setIncludeTest)(e.target.checked)}
        />
        Include test and blocked devices
      </label>

      <div className="text-sm text-content-subtle">
        {!targetId
          ? `Pick ${targetType === 'app' ? 'an app' : 'a PWA'} to see how many devices will receive this.`
          : countingAudience
            ? 'Counting recipients…'
            : `Recipients: ${audience.toLocaleString()} device(s).${includeTest ? '' : ' Test and blocked devices are excluded.'}`}
      </div>

      <div className="flex justify-end gap-3 pt-2">
        <Button type="button" variant="secondary" onClick={onClose}>Cancel</Button>
        {confirming ? (
          <Button
            variant="danger"
            loading={sendMut.isPending}
            onClick={() => sendMut.mutate()}
          >
            {`Confirm — send to ${audience.toLocaleString()}`}
          </Button>
        ) : (
          <Button disabled={!ready} onClick={() => setConfirming(true)}>Send</Button>
        )}
      </div>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export function PushSchedules() {
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['push-schedules'],
    queryFn: listSchedules,
  });
  const schedules = data?.schedules ?? [];

  const inval = () => queryClient.invalidateQueries({ queryKey: ['push-schedules'] });

  const [showNew, setShowNew] = useState(false);
  const [showBroadcast, setShowBroadcast] = useState(false);
  const [editSchedule, setEditSchedule] = useState<PushSchedule | null>(null);

  const deleteMut = useMutation({
    mutationFn: deleteSchedule,
    onSuccess: inval,
  });

  const handlePreview = async (id: string) => {
    try {
      const res = await previewAudience(id);
      alert(`Estimated audience: ${res.audience.toLocaleString()} device(s)`);
    } catch (err) {
      alert(`Preview failed: ${err instanceof Error ? err.message : String(err)}`);
    }
  };

  const handleSendNow = async (id: string, name: string) => {
    if (!confirm(`Send "${name}" immediately to all matching devices?`)) return;
    try {
      const res = await sendNow(id);
      alert(
        `Send complete:\n  Sent: ${res.sent}\n  Failed: ${res.failed}\n  Token invalid: ${res.token_invalid}\n  Skipped: ${res.skipped}`
      );
    } catch (err) {
      alert(`Send failed: ${err instanceof Error ? err.message : String(err)}`);
    }
  };

  const handleTestRun = async (id: string, name: string) => {
    if (!confirm(`Test run "${name}" — send to is_test devices only?`)) return;
    try {
      const res = await testRun(id);
      alert(
        `Test run complete:\n  Sent: ${res.sent}\n  Failed: ${res.failed}\n  Token invalid: ${res.token_invalid}\n  Skipped: ${res.skipped}`
      );
    } catch (err) {
      alert(`Test run failed: ${err instanceof Error ? err.message : String(err)}`);
    }
  };

  return (
    <div>
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-xl sm:text-2xl font-semibold text-content">Push · Schedules</h1>
          <p className="text-sm text-content-subtle mt-1">
            Manage recurring and one-time push notification schedules.
          </p>
        </div>
        <EditOnly>
          <div className="flex gap-3">
            <Button variant="secondary" onClick={() => setShowBroadcast(true)}>Broadcast</Button>
            <Button onClick={() => setShowNew(true)}>New schedule</Button>
          </div>
        </EditOnly>
      </div>

      {isLoading ? (
        <div className="text-sm text-content-subtle">Loading…</div>
      ) : (
        <div className="bg-surface border border-border rounded-lg overflow-hidden">
          <table className="min-w-full text-sm">
            <thead className="bg-surface-muted text-xs text-content-subtle uppercase">
              <tr>
                <th className="text-left px-4 py-3">Name</th>
                <th className="text-left px-4 py-3">Target</th>
                <th className="text-left px-4 py-3">Template</th>
                <th className="text-left px-4 py-3">Kind</th>
                <th className="text-left px-4 py-3">Local time</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-right px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {schedules.map((s) => (
                <tr key={s.id}>
                  <td className="px-4 py-3 font-medium text-content">
                    <Link
                      to={`/push/schedules/${s.id}`}
                      className="text-primary hover:underline"
                    >
                      {s.name}
                    </Link>
                  </td>
                  <td className="px-4 py-3 text-content-muted">
                    <span className="text-xs font-medium text-content-subtle mr-1">
                      {TARGET_TYPE_LABEL[s.target_type ?? 'app']}:
                    </span>
                    {s.target_name ?? s.app_name ?? s.app_id ?? '—'}
                  </td>
                  <td className="px-4 py-3 text-content-muted">{s.template_name ?? s.template_id}</td>
                  <td className="px-4 py-3 text-content-muted capitalize">{s.kind}</td>
                  <td className="px-4 py-3 font-mono text-xs text-content-muted">
                    {minutesToTime(s.send_at_local_minutes)}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`text-xs font-medium ${STATUS_COLORS[s.status] ?? 'text-content-subtle'}`}>
                      {STATUS_LABELS[s.status] ?? s.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap space-x-3">
                    <EditOnly>
                      <button
                        onClick={() => setEditSchedule(s)}
                        className="text-xs text-primary hover:underline"
                      >
                        Edit
                      </button>
                    </EditOnly>
                    <button
                      onClick={() => handlePreview(s.id)}
                      className="text-xs text-content-subtle hover:text-content"
                    >
                      Preview
                    </button>
                    <EditOnly>
                      <button
                        onClick={() => handleSendNow(s.id, s.name)}
                        className="text-xs text-info hover:underline"
                      >
                        Send now
                      </button>
                      <button
                        onClick={() => handleTestRun(s.id, s.name)}
                        className="text-xs text-content-subtle hover:text-content"
                      >
                        Test run
                      </button>
                      <button
                        onClick={() => {
                          if (confirm(`Delete schedule "${s.name}"?`)) deleteMut.mutate(s.id);
                        }}
                        className="text-xs text-danger hover:underline"
                      >
                        Delete
                      </button>
                    </EditOnly>
                  </td>
                </tr>
              ))}
              {schedules.length === 0 && (
                <tr>
                  <td className="px-4 py-8 text-center text-content-subtle text-sm" colSpan={7}>
                    No schedules yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* One-off broadcast modal */}
      <Modal isOpen={showBroadcast} onClose={() => setShowBroadcast(false)} title="Broadcast">
        {showBroadcast && <BroadcastModal onClose={() => setShowBroadcast(false)} />}
      </Modal>

      {/* New schedule modal */}
      <Modal isOpen={showNew} onClose={() => setShowNew(false)} title="New schedule">
        <ScheduleEditorModal schedule={null} onClose={() => setShowNew(false)} />
      </Modal>

      {/* Edit schedule modal */}
      <Modal
        isOpen={!!editSchedule}
        onClose={() => setEditSchedule(null)}
        title={editSchedule ? `Edit schedule · ${editSchedule.name}` : ''}
      >
        {editSchedule && (
          <ScheduleEditorModal
            schedule={editSchedule}
            onClose={() => setEditSchedule(null)}
          />
        )}
      </Modal>
    </div>
  );
}
